<template>
  <div class="ac-body-container">
    <div class="ac-content-container">
      <div class="header">Subscribe to attention alerts</div>
      <div class="sub-header">
        When subscribed, recipients receive email whenever alert is created:
      </div>
      <div class="input-container mt-5">
        <SACDropdown
          v-if="adminRole === 'admin'"
          placeholder="Select Alert"
          :options="filteredOptions"
          field="type"
          :change="(field, value) => addAlert(value)"
          optionLabel="type"
          optionValue="type"
        />
        <div v-if="alertPreferenceList.length" class="table mt-6">
          <div class="table-header">
            <div>Subscribed Alerts</div>
          </div>
          <div class="table-list">
            <div v-for="(alert, idx) in alertPreferenceList" :key="idx" class="table-row">
              <div>{{ alert }}</div>
              <v-icon
                v-if="adminRole === 'admin'"
                color="#FF5B52"
                dark
                medium
                @click="removeAlert(alert)"
              >
                mdi-close-circle-outline
              </v-icon>
            </div>
          </div>
        </div>
        <RecipientSetting readOnly :recipients="emailList" />
      </div>
    </div>
    <div v-if="alertPreferenceList.length" class="ac-content-container">
      <div class="header">Customize Alert Boundary</div>
      <div class="sub-header">
        Here you can customize how Space Temp alerts will trigger notifications
      </div>
      <div
        v-for="(alert, idx) in alertPreferenceList"
        :key="idx"
        class="ac-input-container"
      >
        <div v-if="alert === 'High Space Temp'" class="ac-input-wrapper">
          <div class="ac-input-header">High Space Temp</div>
          <div class="container-item">
            <SACRadio
              name="dynamicHigh"
              field="highType"
              value="dynamic"
              :checked="highType === 'dynamic'"
              :change="handlePreferences"
              label="Dynamic"
              :disable="adminRole !== 'admin'"
            />
            <div class="container-item">
              Triggered whenever the space temperature climbs 6 degrees or more above the
              previous 30 day max for this sensor.
            </div>
          </div>
          <div class="container-item">
            <SACRadio
              name="customHigh"
              field="highType"
              value="custom"
              :checked="highType === 'custom'"
              :change="handlePreferences"
              label="Custom"
              :disable="adminRole !== 'admin'"
            />
          </div>
          <div v-if="errorObj.highTempErr" class="error-msg">
            {{ errorObj.highTempErr }}
          </div>
          <div v-if="highType === 'custom'" class="custom-container">
            <span>Triggers when the space temperature exceeds</span>
            <input
              class="custom-input"
              :value="spaceTempSetting.highTemp"
              @change="(e) => handleChange('highTemp', e.target.value)"
            />
            <span>Degrees F</span>
          </div>
        </div>
        <div v-else-if="alert === 'Low Space Temp'" class="ac-input-wrapper">
          <div class="ac-input-header">Low Space Temp</div>
          <div class="container-item">
            <SACRadio
              name="dynamicLow"
              field="lowType"
              value="dynamic"
              :checked="lowType === 'dynamic'"
              :change="handlePreferences"
              label="Dynamic"
              :disable="adminRole !== 'admin'"
            />
            <div class="container-item">
              Triggered whenever the interior space temperature falls 6 degrees or more
              below the previous 30 day max for this sensor.
            </div>
          </div>
          <div class="container-item">
            <SACRadio
              name="customLow"
              field="lowType"
              value="custom"
              :checked="lowType === 'custom'"
              :change="handlePreferences"
              label="Custom"
              :disable="adminRole !== 'admin'"
            />
          </div>
          <div v-if="errorObj.lowTempErr" class="error-msg">
            {{ errorObj.lowTempErr }}
          </div>
          <div v-if="lowType === 'custom'" class="custom-container">
            <span>Triggers when the space temperature exceeds</span>
            <input
              class="custom-input"
              :value="spaceTempSetting.lowTemp"
              @change="(e) => handleChange('lowTemp', e.target.value)"
            />
            <span>Degrees F</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import partnerSettingsMixin from "@shared/mixins/partnerSettingsMixin";

import SACDropdown from "@shared/components/sac/SACDropdown.vue";
import SACRadio from "@shared/components/sac/SACRadio.vue";
import RecipientSetting from "./shared/RecipientSetting.vue";

export default {
  name: "AlertPreference",
  components: {
    SACDropdown,
    SACRadio,
    RecipientSetting,
  },
  mixins: [partnerSettingsMixin],
  props: {
    emailList: Array,
    alertOptions: Array,
    alertPreferenceList: Array,
    spaceTempSetting: Object,
    addAlert: Function,
    removeAlert: Function,
    toggleActionModal: Function,
    onChange: Function,
    errorObj: Object,
  },
  data() {
    const { alertOptions, spaceTempSetting } = this;
    const filteredOptions = alertOptions.filter((option) =>
      option.type.includes("Space")
    );
    let highType = null;
    let lowType = null;
    if (spaceTempSetting) {
      highType = spaceTempSetting.highTemp ? "custom" : "dynamic";
      lowType = spaceTempSetting.lowTemp ? "custom" : "dynamic";
    }
    return {
      filteredOptions,
      highType,
      lowType,
    };
  },
  methods: {
    handleChange(field, value) {
      const newSpaceTempSetting = {
        ...this.spaceTempSetting,
        [field]: value,
      };
      this.onChange("spaceTempSetting", newSpaceTempSetting);
    },
    handlePreferences(field, value) {
      const newSpaceTempSetting = { ...this.spaceTempSetting };

      if (field === "highType") {
        if (value === "dynamic") {
          newSpaceTempSetting.highTemp = null;
          newSpaceTempSetting.highPref = "dynamic";
        } else if (value === "custom" && !newSpaceTempSetting.highTemp) {
          newSpaceTempSetting.highTemp = 85;
          newSpaceTempSetting.highPref = "custom";
        }
      }

      if (field === "lowType") {
        if (value === "dynamic") {
          newSpaceTempSetting.lowPref = "dynamic";
          newSpaceTempSetting.lowTemp = null;
        } else if (value === "custom" && !newSpaceTempSetting.lowTemp) {
          newSpaceTempSetting.lowPref = "custom";
          newSpaceTempSetting.lowTemp = 54;
        }
      }

      this[field] = value;
      this.onChange("spaceTempSetting", newSpaceTempSetting);
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  font-size: 1rem;
  font-weight: 700;
  color: $N80;
}

.sub-header {
  font-size: 1rem;
  font-weight: 500;
  color: $N60;
}

.custom-input {
  border: 1px solid $N10;
  border-radius: 4px;
  width: 50px;
  height: 28px;
  padding-left: 4px;
  font-size: 0.75em;

  span {
    white-space: nowrap;
  }
}

.custom-container {
  display: grid;
  grid-template-columns: auto 0.1fr 1fr;
  margin-left: 60px;
  grid-gap: 10px;
}

.error-msg {
  margin-left: 60px;
  width: 100%;
  color: #ff5b52 !important;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 5px;
  margin-bottom: 5px;
}

.input-container {
  width: 60%;
}

.ac-content-container {
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 2px solid $N20;
  .container-item {
    padding-left: 30px;
  }
}

.ac-input-container {
  margin-top: 20px;
  width: 60%;

  .ac-input-header {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 3px;
  }
}

.table {
  border: 2px solid $NBLUE20;
  border-radius: 6px;

  .table-header {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr auto;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    padding: 8px 10px;
    font-weight: 600;
    background-color: $NBLUE10;
    color: $N100;

    .edit-btn {
      padding: 0 16px;
      height: 30px;
      border-radius: 6px;
      background-color: $NAVY100;
      color: $WHITE;
      font-weight: 600;
    }
  }

  .table-list {
    display: grid;
    overflow: auto;
    max-height: 300px;

    .table-row {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      justify-content: flex-start;
      height: 45px;
      padding: 0 12px;
      border-top: 2px solid $NBLUE20;
      font-size: 1rem;
      font-weight: 500;
      color: $N80;
    }
  }
}
</style>
